import { toMajor, type CurrencyCode } from '../domain/money';

/**
 * Formatting rules:
 *  - amounts, numbers and numeric dates follow the billing region
 *    (fr-FR -> "1 234,50 €", 28/09/2026)
 *  - words (month names) follow the app language ("September" / "Eylül")
 * Intl formatters are cached: month switching must feel instant.
 */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function numberFormat(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `n|${locale}|${JSON.stringify(opts)}`;
  let f = cache.get(key) as Intl.NumberFormat | undefined;
  if (!f) cache.set(key, (f = new Intl.NumberFormat(locale, opts)));
  return f;
}

function dateFormat(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `d|${locale}|${JSON.stringify(opts)}`;
  let f = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (!f) cache.set(key, (f = new Intl.DateTimeFormat(locale, opts)));
  return f;
}

export interface MoneyFormatOptions {
  /** Drop the minor units ("$2,840" instead of "$2,840.00"); used for headline totals. */
  whole?: boolean;
  /** Always show the sign ("+$20", "−$16"); zero stays unsigned. */
  signed?: boolean;
}

export function formatMoney(
  minor: number,
  currency: CurrencyCode,
  formatLocale: string,
  { whole = false, signed = false }: MoneyFormatOptions = {},
): string {
  const opts: Intl.NumberFormatOptions = { style: 'currency', currency };
  if (whole) opts.maximumFractionDigits = opts.minimumFractionDigits = 0;
  if (signed) opts.signDisplay = 'exceptZero';
  // A true minus sign lines up with "+" in tabular columns; hyphen-minus does not.
  return numberFormat(formatLocale, opts).format(toMajor(minor, currency)).replace('-', '−');
}

export function formatPercent(
  ratio: number,
  formatLocale: string,
  { signed = true, digits = 1 }: { signed?: boolean; digits?: number } = {},
): string {
  return numberFormat(formatLocale, {
    style: 'percent',
    maximumFractionDigits: digits,
    signDisplay: signed ? 'exceptZero' : 'never',
  })
    .format(ratio)
    .replace('-', '−');
}

/** "September 2026" / "Eylül 2026". month is 1-12. */
export function formatMonthYear(year: number, month: number, appLanguage: string): string {
  return dateFormat(appLanguage, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(Date.UTC(year, month - 1, 1));
}

/** "August" / "Ağustos". month is 1-12. */
export function formatMonthName(month: number, appLanguage: string, style: 'long' | 'short' = 'long'): string {
  return dateFormat(appLanguage, { month: style, timeZone: 'UTC' }).format(Date.UTC(2000, month - 1, 1));
}

/** "Oct 1" / "1 Eki": short day + month, words in the app language. */
export function formatDayMonth(isoDate: string, appLanguage: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return dateFormat(appLanguage, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(Date.UTC(y!, m! - 1, d!));
}

/** Numeric date in the billing region's order: 09/28/2026 (US), 28/09/2026 (FR). */
export function formatShortDate(isoDate: string, formatLocale: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return dateFormat(formatLocale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(
    Date.UTC(y!, m! - 1, d!),
  );
}

/** Regions whose calendars start on Sunday (for engines without Intl week info). */
const SUNDAY_FIRST = new Set(['US', 'CA', 'JP', 'IL', 'BR', 'MX', 'PH', 'KR', 'TW', 'HK', 'IN', 'ZA', 'SA']);

/**
 * The first day of the week in the billing region's calendars, Monday = 0 …
 * Sunday = 6 (the week starts on Sunday in the US, on Monday in Türkiye).
 */
export function firstWeekday(formatLocale: string): number {
  try {
    const locale = new Intl.Locale(formatLocale) as Intl.Locale & { getWeekInfo?: () => { firstDay: number }; weekInfo?: { firstDay: number } };
    const firstDay = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay;
    if (firstDay) return (firstDay - 1) % 7;
    return SUNDAY_FIRST.has(locale.maximize().region ?? '') ? 6 : 0;
  } catch {
    return 0;
  }
}

/** Short weekday names in the app language, Monday first ("Mon", "Pzt"). */
export function weekdayNames(appLanguage: string, style: 'short' | 'narrow' | 'long' = 'short'): string[] {
  // 2026-09-28 is a Monday.
  return Array.from({ length: 7 }, (_, i) => dateFormat(appLanguage, { weekday: style, timeZone: 'UTC' }).format(Date.UTC(2026, 8, 28 + i)));
}

/** A moment as a full date in the app language: 30 September 2026 (the device's time zone). */
export function formatLongDate(ms: number, appLanguage: string): string {
  return dateFormat(appLanguage, { day: 'numeric', month: 'long', year: 'numeric' }).format(ms);
}
